/**
 * WorkInvoiceCard Component (DA-314)
 *
 * Displays work invoice information for completed duties.
 * Shows invoice metadata, compensation breakdown, and download button.
 * Used on both substitute and provider service detail pages.
 */

import * as React from 'react';
import { useConvex } from 'convex/react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, Clock3, Download, FileCheck2, FileText, Loader2 } from 'lucide-react';
import { toast } from '@/template/lib/toast';
import { type WorkInvoiceCardProps } from '@/types/substitute-components';
import i18n from '@/i18n';
import { formatCentsCurrency } from '@/lib/formatters';
import { formatBerlinDateWithOptions } from '@/lib/berlin-time';

/**
 * Returns status badge configuration
 */
const getStatusBadge = (status: 'draft' | 'issued' | 'paid' | 'superseded'): { variant: 'default' | 'secondary' | 'success'; label: string; Icon: typeof Clock3 } => {
  const statusConfig: Record<'draft' | 'issued' | 'paid' | 'superseded', { variant: 'default' | 'secondary' | 'success'; label: string; Icon: typeof Clock3 }> = {
    draft: { variant: 'secondary', label: 'Entwurf', Icon: Clock3 },
    issued: { variant: 'default', label: 'Ausgestellt', Icon: FileCheck2 },
    paid: { variant: 'success', label: 'Bezahlt', Icon: CheckCircle2 },
    superseded: { variant: 'secondary', label: 'Ersetzt', Icon: FileText },
  };

  return statusConfig[status];
};

export const WorkInvoiceCard = ({ invoice, viewerRole }: WorkInvoiceCardProps): React.ReactElement => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const convex = useConvex();

  const handleDownload = async (): Promise<void> => {
    if (!invoice.storageId) {
      toast.error(i18n.t('common:toasts.workInvoice.pdfUnavailable'));
      return;
    }

    setIsDownloading(true);

    try {
      // Get signed URL from Convex storage
      const url = await convex.query(api.app.storage.queries.getStorageUrl, {
        storageId: invoice.storageId,
      });

      if (!url) {
        throw new Error('Could not generate download URL');
      }

      // Download the PDF
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${invoice.invoiceNumber || 'Rechnung'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(i18n.t('common:toasts.workInvoice.downloaded'));
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error(i18n.t('common:toasts.workInvoice.downloadFailed'));
    } finally {
      setIsDownloading(false);
    }
  };

  const statusBadge = getStatusBadge(invoice.status);
  const hasNotfallscheinbeteiligung = invoice.notfallscheinbeteiligungAmount && invoice.notfallscheinbeteiligungAmount > 0;

  return (
    <Card data-testid='work-invoice-card' className='@container/card'>
      <CardHeader data-testid='work-invoice-card-header'>
        <div data-testid='work-invoice-card-header-content' className='flex flex-col gap-3 @sm/card:flex-row @sm/card:items-start @sm/card:justify-between'>
          <div className='flex min-w-0 items-start gap-3'>
            <div className='shrink-0 rounded-lg bg-primary/10 p-2.5'>
              <FileText className='size-5 text-primary' aria-hidden='true' />
            </div>
            <div className='min-w-0'>
              <CardTitle className='break-words text-lg'>{viewerRole === 'substitute' ? 'Ihre Rechnung' : 'Rechnung vom Vertreter'}</CardTitle>
              <CardDescription className='break-words'>
                <span className='block break-all font-medium text-foreground'>Rechnungsnummer: {invoice.invoiceNumber || 'nicht verfügbar'}</span>
                <span className='block'>Ausgestellt am {formatBerlinDateWithOptions(invoice.issuedAt, { day: '2-digit', month: 'long', year: 'numeric' })}</span>
              </CardDescription>
            </div>
          </div>
          <Badge className='shrink-0 self-start' variant={statusBadge.variant}>
            <statusBadge.Icon className='mr-1 size-3.5' aria-hidden='true' />
            {statusBadge.label}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className='space-y-4'>
        {/* Amounts Breakdown */}
        <div className='space-y-3'>
          <div data-testid='work-invoice-card-amount-row' className='flex flex-col gap-1 text-sm @sm/card:flex-row @sm/card:items-baseline @sm/card:justify-between'>
            <span className='break-words text-muted-foreground'>Stundenhonorar:</span>
            <span className='shrink-0 font-medium'>{formatCentsCurrency(invoice.baseAmount)}</span>
          </div>

          {hasNotfallscheinbeteiligung && (
            <div className='flex flex-col gap-1 text-sm @sm/card:flex-row @sm/card:items-baseline @sm/card:justify-between'>
              <span className='break-words text-muted-foreground'>Notfallscheinbeteiligung:</span>
              <span className='shrink-0 font-medium text-success'>{formatCentsCurrency(invoice.notfallscheinbeteiligungAmount!)}</span>
            </div>
          )}

          <Separator />

          <div className='flex flex-col gap-1 @sm/card:flex-row @sm/card:items-baseline @sm/card:justify-between'>
            <span className='break-words font-semibold'>Gesamtbetrag:</span>
            <span className='shrink-0 text-lg font-bold'>{formatCentsCurrency(invoice.amount)}</span>
          </div>
        </div>

        {/* Tax Info */}
        <div className='rounded-lg bg-muted/50 p-3'>
          <p className='text-xs text-muted-foreground'>
            <strong>Steuerhinweis:</strong> Die erbrachte Leistung ist gemäß § 4 Nr. 14 UStG von der Umsatzsteuer befreit.
          </p>
        </div>
      </CardContent>

      <CardFooter data-testid='work-invoice-card-footer' className='flex flex-col items-stretch gap-2 @sm/card:flex-row @sm/card:items-center @sm/card:justify-between'>
        <div className='flex min-w-0 items-center gap-2 text-xs text-muted-foreground'>
          <FileText className='size-3' aria-hidden='true' />
          <span className='break-words'>PDF-Rechnung</span>
        </div>
        <div className='flex min-w-0 flex-col items-stretch gap-1 @sm/card:items-end'>
          {!invoice.storageId && <span className='text-xs text-muted-foreground'>PDF noch nicht verfügbar</span>}
          <Button
            data-testid='work-invoice-card-download'
            className='min-h-11 w-full @sm/card:w-auto'
            onClick={handleDownload}
            disabled={isDownloading || !invoice.storageId}
            size='sm'
            aria-describedby={!invoice.storageId ? 'work-invoice-card-download-help' : undefined}
          >
            {isDownloading ? (
              <>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' aria-hidden='true' />
                Lädt...
              </>
            ) : (
              <>
                <Download className='mr-2 h-4 w-4' aria-hidden='true' />
                Herunterladen
              </>
            )}
          </Button>
          {!invoice.storageId && (
            <span id='work-invoice-card-download-help' className='sr-only'>
              Die PDF-Rechnung steht noch nicht zum Download bereit.
            </span>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
